import * as THREE from 'three';
import { SceneManager } from './scene/SceneManager.js';
import { Skybox } from './scene/Skybox.js';
import { BlackHole } from './objects/BlackHole.js';
import { LightSource } from './objects/LightSource.js';
import { BlackHoleShader } from './shaders/BlackHoleShader.js';
import { CameraController } from './controls/CameraController.js';
import { ObjectPlacer } from './controls/ObjectPlacer.js';
import { ControlPanel } from './ui/ControlPanel.js';

class BlackHoleSimulator {
    constructor() {
        this.sceneManager = null;
        this.skybox = null;
        this.blackHole = null;
        this.lightSources = [];
        this.lensingShader = null;
        this.cameraController = null;
        this.objectPlacer = null;
        this.controlPanel = null;
        
        this.isPlacementMode = false;
        this.lastTime = 0;
        
        this.init();
    }

    init() {
        // Initialize scene
        const container = document.getElementById('canvas').parentElement;
        this.sceneManager = new SceneManager(container);
        
        // Add skybox
        this.skybox = new Skybox(this.sceneManager.getScene(), 5000);
        
        // Create black hole at origin
        this.blackHole = new BlackHole(new THREE.Vector3(0, 0, 0), 10);
        this.sceneManager.getScene().add(this.blackHole.getGroup());
        
        // Initialize camera controller
        this.cameraController = new CameraController(
            this.sceneManager.getCamera(),
            this.sceneManager.getRenderer()
        );
        
        // Initialize object placer
        this.objectPlacer = new ObjectPlacer(
            this.sceneManager.getScene(),
            this.sceneManager.getCamera(),
            this.sceneManager.getRenderer()
        );
        
        // Initialize lensing shader
        this.lensingShader = new BlackHoleShader(
            this.blackHole,
            this.sceneManager.getScene(),
            this.sceneManager.getCamera()
        );
        
        // Initialize control panel
        this.controlPanel = new ControlPanel(
            document.getElementById('control-panel'),
            {
                onMassChange: (mass) => this.onBlackHoleMassChange(mass),
                onAddLight: () => this.onAddLightSource(),
                onRemoveLight: (lightId) => this.onRemoveLightSource(lightId),
                onLightIntensityChange: (lightId, intensity) => this.onLightIntensityChange(lightId, intensity),
                onLightColorChange: (lightId, color) => this.onLightColorChange(lightId, color),
                onCameraPreset: (preset) => this.onCameraPreset(preset)
            }
        );
        
        // Add initial light source
        this.addLightSource(new THREE.Vector3(20, 10, 20), 10, 0xffffff);
        
        // Initialize info panel toggle
        this.initInfoPanelToggle();
        
        // Start animation loop
        this.animate();
        
        // Handle placement mode toggle
        document.addEventListener('keydown', (e) => {
            if (e.key === 'p' || e.key === 'P') {
                this.togglePlacementMode();
            }
        });
    }

    initInfoPanelToggle() {
        const infoPanel = document.getElementById('info');
        const toggleBtn = document.getElementById('info-toggle');
        
        if (infoPanel && toggleBtn) {
            // Ensure position is set correctly (CSS may be overridden)
            infoPanel.style.position = 'absolute';
            infoPanel.style.top = '20px';
            infoPanel.style.left = '20px';
            
            toggleBtn.addEventListener('click', () => {
                const isMinimized = infoPanel.classList.toggle('minimized');
                toggleBtn.textContent = isMinimized ? '+' : '−';
                toggleBtn.title = isMinimized ? 'Maximize panel' : 'Minimize panel';
            });
        }
    }

    addLightSource(position, intensity = 10, color = 0xffffff) {
        const light = new LightSource(position, intensity, color);
        this.lightSources.push(light);
        this.sceneManager.getScene().add(light.getGroup());
        this.controlPanel.addLightSourceItem(light, this.lightSources.length - 1);
        return light;
    }

    onAddLightSource() {
        // Enable placement mode
        this.togglePlacementMode();
    }

    togglePlacementMode() {
        this.isPlacementMode = !this.isPlacementMode;
        
        if (this.isPlacementMode) {
            this.objectPlacer.enable((position) => {
                this.addLightSource(position, 10, 0xffffff);
                this.objectPlacer.disable();
                this.isPlacementMode = false;
            });
        } else {
            this.objectPlacer.disable();
        }
    }

    onRemoveLightSource(lightId) {
        const index = this.lightSources.findIndex(light => light.getId() === lightId);
        if (index !== -1) {
            const light = this.lightSources[index];
            this.sceneManager.getScene().remove(light.getGroup());
            light.dispose();
            this.lightSources.splice(index, 1);
            this.controlPanel.removeLightSourceItem(lightId);
        }
    }

    onLightIntensityChange(lightId, intensity) {
        const light = this.lightSources.find(l => l.getId() === lightId);
        if (light) {
            light.setIntensity(intensity);
        }
    }

    onLightColorChange(lightId, color) {
        const light = this.lightSources.find(l => l.getId() === lightId);
        if (light) {
            light.setColor(color);
        }
    }

    onBlackHoleMassChange(mass) {
        if (this.blackHole) {
            this.blackHole.setMass(mass);
        }
    }

    onCameraPreset(preset) {
        if (this.cameraController) {
            this.cameraController.setPreset(preset);
        }
    }

    animate() {
        const currentTime = performance.now();
        const deltaTime = currentTime - this.lastTime;
        this.lastTime = currentTime;
        
        // Update camera controls
        this.cameraController.update();
        
        // Update black hole
        if (this.blackHole) {
            this.blackHole.update(deltaTime);
        }
        
        // Update light sources
        this.lightSources.forEach(light => light.update(deltaTime));
        
        // Update skybox
        if (this.skybox) {
            this.skybox.update(deltaTime);
        }
        
        // Update lensing shader
        if (this.lensingShader) {
            this.lensingShader.update(
                this.sceneManager.getRenderer(),
                this.sceneManager.getScene(),
                this.sceneManager.getCamera()
            );
            
            // Render with lensing effect
            this.lensingShader.render(
                this.sceneManager.getRenderer(),
                this.sceneManager.getScene(),
                this.sceneManager.getCamera()
            );
        } else {
            // Fallback to normal rendering
            this.sceneManager.render();
        }
        
        requestAnimationFrame(() => this.animate());
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new BlackHoleSimulator();
    });
} else {
    new BlackHoleSimulator();
}
